import { useEffect, useMemo, useState } from "react";

const SEARCH_FIELDS = ["Portal", "Code", "Marketing_Name", "Programmes", "Module_Type", "Duration", "topics"];

/**
 * Distinct, non-empty values of one field across the rows, as `{ value, label }` filter options.
 * @param {Array<object>} rows Module rows.
 * @param {string} field Row field to collect.
 */
function getDistinctOptions(rows, field) {
    return Array.from(new Set(
        rows.map((row) => row[field]).filter((value) => value !== null && value !== undefined && value !== "")
    )).map((value) => ({ value: String(value), label: String(value) }));
}

/**
 * Client-side search/status/term/year filtering, sorting and pagination over an unpaginated
 * module list - shared by the Version Modules table and its Assign Module modal.
 * @param {Array<object>} rows Every module row to filter, sort and page.
 */
function useVersionModuleRows(rows) {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState([]);
    const [termFilter, setTermFilter] = useState([]);
    const [yearFilter, setYearFilter] = useState([]);

    const [sortKey, setSortKey] = useState("Portal");
    const [sortOrder, setSortOrder] = useState("ASC");

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const statusOptions = useMemo(() => getDistinctOptions(rows, "module_status"), [rows]);
    const termOptions = useMemo(() => getDistinctOptions(rows, "term"), [rows]);
    const yearOptions = useMemo(() => getDistinctOptions(rows, "year"), [rows]);

    const filteredRows = useMemo(() => {
        const searchTerm = search.trim().toLowerCase();

        return rows.filter((row) => {
            if (searchTerm) {
                const matchesSearch = SEARCH_FIELDS.some(
                    (field) => Boolean(row[field]) && String(row[field]).toLowerCase().includes(searchTerm)
                );

                if (!matchesSearch) {
                    return false;
                }
            }

            if (statusFilter.length && !statusFilter.includes(row.module_status)) {
                return false;
            }

            if (termFilter.length && !termFilter.includes(String(row.term))) {
                return false;
            }

            if (yearFilter.length && !yearFilter.includes(String(row.year))) {
                return false;
            }

            return true;
        });
    }, [rows, search, statusFilter, termFilter, yearFilter]);

    const sortedRows = useMemo(() => {
        const sorted = [...filteredRows].sort((a, b) => {
            const aValue = a[sortKey];
            const bValue = b[sortKey];

            if (aValue === bValue) {
                return 0;
            }

            if (aValue === null || aValue === undefined) {
                return -1;
            }

            if (bValue === null || bValue === undefined) {
                return 1;
            }

            return aValue > bValue ? 1 : -1;
        });

        return sortOrder === "ASC" ? sorted : [...sorted].reverse();
    }, [filteredRows, sortKey, sortOrder]);

    const totalRows = sortedRows.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
    const pagedRows = sortedRows.slice((page - 1) * perPage, page * perPage);

    const rangeStart = totalRows === 0 ? 0 : (page - 1) * perPage + 1;
    const rangeEnd = Math.min(page * perPage, totalRows);

    const filterSignature = `${search}|${statusFilter.join(",")}|${termFilter.join(",")}|${yearFilter.join(",")}`;

    useEffect(() => {
        setPage(1);
    }, [filterSignature]);

    /* Keeps the current page valid after the row count shrinks (unlink/assign), or perPage changes. */
    useEffect(() => {
        setPage((current) => Math.min(current, totalPages));
    }, [totalPages]);

    const resetFilters = () => {
        setSearch("");
        setStatusFilter([]);
        setTermFilter([]);
        setYearFilter([]);
        setPage(1);
    };

    const changeSort = (field, nextOrder) => {
        setPage(1);
        setSortKey(field);
        setSortOrder(nextOrder);
    };

    return {
        search,
        setSearch,
        statusFilter,
        setStatusFilter,
        statusOptions,
        termFilter,
        setTermFilter,
        termOptions,
        yearFilter,
        setYearFilter,
        yearOptions,
        resetFilters,
        filterSignature,
        rows: pagedRows,
        allFilteredRows: sortedRows,
        totalRows,
        totalPages,
        page,
        setPage,
        perPage,
        setPerPage,
        rangeStart,
        rangeEnd,
        sortKey,
        sortOrder,
        changeSort,
    };
}

export default useVersionModuleRows;
